import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Order, PackagingDetail, PackingPayloadPackagingItem, PreparationWithProducts } from '../models';
import { useToast } from '@shared/hooks/use-toast';
import { filterLinePicksForOrder, uniqueBatchesFromPicks } from '../utils/linePicksForOrder';
import {
  areAllPackagingsCompleted,
  buildPackagingPayload,
  computeOrderPackagingRequirements,
  findPackagingByCode,
  findRequirementToFulfill,
  packagingAssignKey,
  requirementUsesStationScan,
  sumAssignedForRequirement,
} from '../utils/packagingRequirements';
import {
  AssignedPackagingScanDetail,
  findRequirementForStationLocation,
  findStationLocationByCode,
  orderRequiresPackingStation,
  packagingScanDetailKey,
  type PackingStationSelection,
} from '../utils/packingStationPackaging';
import { packingAssignKey, packingScanCodesMatch } from '../utils/packingAssignKey';
import { orderHasAttachments } from '../utils/orderAttachments';
import { sortOrdersByCourierThenId } from '../utils/sortOrdersByCourierThenId';
import { usePackingService } from './usePackingService';

const batchAllocKey = (assignKey: string, batchId: number) => `${assignKey}@@${batchId}`;
// Modo temporal para pruebas locales: evita enviar el packing al backend.
const TEMP_DISABLE_PACKING_API = false;

// Información del producto seleccionado
export interface SelectedProductInfo {
  id: string;
  code: string;
  name: string;
}

// Estado del flujo de packing
export interface PackingFlowState {
  isPackingMode: boolean;
  selectedProduct: SelectedProductInfo | null;
  selectedOrder: Order | null;
  // Cantidades asignadas por orden y línea: key `${orderId}:${lineId}:${productCode}`
  assignedQuantities: Record<string, number>;
  completedOrder: Order | null;
  isSendingOrder: boolean;
  packingError: string | null;
  /** Lote elegido para el próximo escaneo cuando hay varios lotes (product_id → batch_id) */
  activeBatchIdByProduct: Record<number, number | null>;
}

export interface PackingFlowApi extends PackingFlowState {
  selectProduct: (productCode: string) => void;
  addToPacking: (productCode: string, quantity: number, orderId?: number, batchId?: number | null) => void;
  getMissingQtyFor: (productCode: string) => number;
  exitPackingMode: () => void;
  processScannedCode: (code: string) => Promise<{ success: boolean; message: string }>;
  clearPackingError: () => void;
  setActiveBatchIdForProduct: (productId: number, batchId: number | null) => void;
}

export const usePackingFlow = (
  preparation: PreparationWithProducts | null,
  orders: Order[] = [],
  refreshData?: () => void,
  packagings: PackagingDetail[] = [],
  attachmentPrinterCode?: string | null,
  packingStation?: PackingStationSelection | null,
) => {
  const { toast } = useToast();
  const [isPackingMode, setIsPackingMode] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<SelectedProductInfo | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [assignedQuantities, setAssignedQuantities] = useState<Record<string, number>>({});
  /** Unidades escaneadas por packaging real (no por requerimiento): key `${orderId}:pkg:${packagingId}` */
  const [assignedPackagingQuantities, setAssignedPackagingQuantities] = useState<Record<string, number>>({});
  const [assignedPackagingPayloadItems, setAssignedPackagingPayloadItems] = useState<
    Record<string, Omit<PackingPayloadPackagingItem, 'qty'>>
  >({});
  const [assignedPackagingScanDetails, setAssignedPackagingScanDetails] = useState<
    Record<string, AssignedPackagingScanDetail>
  >({});
  const [completedOrder, setCompletedOrder] = useState<Order | null>(null);
  const [isSendingOrder, setIsSendingOrder] = useState<boolean>(false);
  const [packingError, setPackingError] = useState<string | null>(null);
  const [lastAddedQuantity, setLastAddedQuantity] = useState<{
    key: string;
    quantity: number;
    batchId?: number;
  } | null>(null);
  const [batchAllocations, setBatchAllocations] = useState<Record<string, number>>({});
  const [activeBatchIdByProduct, setActiveBatchIdByProduct] = useState<Record<number, number | null>>({});
  const packingService = usePackingService();
  /** Candado síncrono: impide segundo PUT /pack antes de que pinte isSendingOrder (races efecto/useState). */
  const packHttpInFlightRef = useRef(false);
  /**
   * Pedidos enviados correctamente a la API pero cuyo `orders` aún no trae `shipment.processed_at`.
   * Evita reelegirlos si el usuario escanea en el hueco entre éxito del pack y el refetch.
   */
  const packedPendingRefreshRef = useRef<Set<number>>(new Set());

  const ordersByPackingPriority = useMemo(() => sortOrdersByCourierThenId(orders), [orders]);

  useEffect(() => {
    const pending = packedPendingRefreshRef.current;
    if (pending.size === 0) return;
    for (const o of orders) {
      if (o.shipment?.processed_at && pending.has(o.id)) {
        pending.delete(o.id);
      }
    }
  }, [orders]);

  // Busca el primer pedido que requiere el producto
  const findFirstOrderNeedingProduct = useCallback(
    (productCode: string): Order | null => {
      return (
        ordersByPackingPriority.find((order: Order) => {
          if (order.shipment?.processed_at) return false; // Pedido ya completado
          if (packedPendingRefreshRef.current.has(order.id)) return false;

          return (order.lines || []).some((line) => {
            if (line.pack && line.pack.lines) {
              // Es un pack - verificar cada producto del pack
              return line.pack.lines.some((packLine) => {
                if (!packingScanCodesMatch(packLine.product?.code, productCode)) return false;
                // Para packs, incluir productCode en la key
                const key = packingAssignKey(order.id, line.id, productCode);
                const assigned = assignedQuantities[key] || 0;
                // La cantidad esperada es cantidad del producto × cantidad de packs
                const expectedQty = (packLine.qty || 1) * (line.qty || 1);
                const remaining = expectedQty - assigned;
                return remaining > 0;
              });
            } else if (line.product) {
              // Es un producto individual
              if (!packingScanCodesMatch(line.product.code, productCode)) return false;
              const key = packingAssignKey(order.id, line.id, productCode);
              const assigned = assignedQuantities[key] || 0;
              const remaining = (line.qty || 0) - assigned;
              return remaining > 0;
            }
            return false;
          });
        }) || null
      );
    },
    [ordersByPackingPriority, assignedQuantities],
  );

  // Selecciona producto y orden objetivo; habilita modo packing
  const selectProduct = useCallback(
    (productCode: string) => {
      if (!preparation) return;

      // Buscar el producto en la preparación
      const foundLine = preparation.lines.find(
        (line) =>
          packingScanCodesMatch(line.product?.code, productCode) ||
          packingScanCodesMatch(line.product?.ean, productCode),
      );

      if (!foundLine) return;

      const selected: SelectedProductInfo = {
        id: foundLine.product_id.toString(),
        code: productCode,
        name: foundLine.product?.name || 'Producto',
      };

      setSelectedProduct(selected);
      setIsPackingMode(true);
      setCompletedOrder(null); // Limpiar alerta de pedido completado al iniciar uno nuevo

      // Si ya estamos en modo packing con una orden seleccionada, mantener esa orden
      // Solo buscar una nueva orden si no hay una seleccionada
      if (selectedOrder) {
        // Verificar si la orden actual está completada
        if (selectedOrder.shipment?.processed_at || packedPendingRefreshRef.current.has(selectedOrder.id)) {
          // La orden está completada, buscar una nueva orden para este producto
          const targetOrder = findFirstOrderNeedingProduct(productCode);
          setSelectedOrder(targetOrder);
          return;
        }

        // Verificar si el producto seleccionado está en la orden actual
        const isProductInCurrentOrder = (selectedOrder.lines || []).some((line) => {
          if (line.pack && line.pack.lines) {
            // Es un pack - verificar cada producto del pack
            return line.pack.lines.some((packLine) => packingScanCodesMatch(packLine.product?.code, productCode));
          } else if (line.product) {
            // Es un producto individual
            return packingScanCodesMatch(line.product?.code, productCode);
          }
          return false;
        });

        if (isProductInCurrentOrder) {
          // Mantener la orden actual si el producto está en ella
          return;
        }
      }

      // Solo buscar una nueva orden si no hay una seleccionada o si el producto no está en la orden actual
      const targetOrder = findFirstOrderNeedingProduct(productCode);
      setSelectedOrder(targetOrder);
    },
    [preparation, findFirstOrderNeedingProduct, selectedOrder],
  );

  // Agrega una cantidad al packing (solo en memoria)
  const addToPacking = useCallback(
    (
      productCode: string,
      quantity: number,
      orderId?: number,
      batchId?: number | null,
    ): { success: boolean; message: string; quantityToAdd?: number; targetOrder?: Order; key?: string } => {
      const targetOrder = orderId
        ? orders?.find((o: Order) => o.id === orderId)
        : findFirstOrderNeedingProduct(productCode);

      if (!targetOrder) {
        console.error('No se encontró una orden válida para este producto');
        return { success: false, message: 'No se encontró una orden válida para este producto' };
      }

      // Encontrar la primera línea que necesita este producto
      let targetLine: any = null;
      let isFromPack = false;

      // Buscar en líneas de productos individuales
      for (const line of targetOrder.lines || []) {
        if (packingScanCodesMatch(line.product?.code, productCode)) {
          const key = packingAssignKey(targetOrder.id, line.id, productCode);
          const assigned = assignedQuantities[key] || 0;
          const remaining = (line.qty || 0) - assigned;
          if (remaining > 0) {
            targetLine = line;
            isFromPack = false;
            break;
          }
        }
      }

      // Si no se encuentra en productos individuales, buscar en packs
      if (!targetLine) {
        for (const line of targetOrder.lines || []) {
          if (line.pack && line.pack.lines) {
            const packProduct = line.pack.lines.find((packLine) =>
              packingScanCodesMatch(packLine.product?.code, productCode),
            );
            if (packProduct) {
              // Para packs, incluir productCode en la key para diferenciar productos
              const key = packingAssignKey(targetOrder.id, line.id, productCode);
              const assigned = assignedQuantities[key] || 0;
              // La cantidad esperada es cantidad del producto × cantidad de packs
              const expectedQty = (packProduct.qty || 1) * (line.qty || 1);
              const remaining = expectedQty - assigned;
              if (remaining > 0) {
                targetLine = line;
                isFromPack = true;
                break;
              }
            }
          }
        }
      }

      if (!targetLine) {
        console.warn('No se encontró una línea pendiente para este producto');
        return {
          success: false,
          message: 'Este producto ya está completo en el pedido',
        };
      }

      // Calcular cantidad máxima para esta línea específica
      // Para packs: incluir productCode en la key
      const key = packingAssignKey(targetOrder.id, targetLine.id, productCode);
      const currentQuantity = assignedQuantities[key] || 0;

      let maxQuantityNeeded = 0;
      if (isFromPack) {
        const packProduct = targetLine.pack!.lines.find((packLine: any) =>
          packingScanCodesMatch(packLine.product?.code, productCode),
        );
        // La cantidad esperada es cantidad del producto × cantidad de packs
        maxQuantityNeeded = (packProduct?.qty || 1) * (targetLine.qty || 1);
      } else {
        maxQuantityNeeded = targetLine.qty || 0;
      }

      // Validar que no se exceda la cantidad máxima
      const remainingNeeded = maxQuantityNeeded - currentQuantity;
      if (remainingNeeded <= 0) {
        console.warn('Ya se ha completado la cantidad requerida para esta línea');
        return {
          success: false,
          message: 'Este producto ya está completo en el pedido',
        };
      }

      // Ajustar la cantidad a agregar si excede lo necesario
      const quantityToAdd = Math.min(quantity, remainingNeeded);

      // Actualizar assignedQuantities inmediatamente (se revertirá si la API falla)
      const newQuantity = currentQuantity + quantityToAdd;
      setAssignedQuantities((prev) => ({
        ...prev,
        [key]: newQuantity,
      }));

      if (batchId != null && batchId > 0) {
        const bKey = batchAllocKey(key, batchId);
        setBatchAllocations((prev) => ({
          ...prev,
          [bKey]: (prev[bKey] || 0) + quantityToAdd,
        }));
      }

      if (quantityToAdd < quantity) {
        return {
          success: true,
          message: `Se agregaron ${quantityToAdd} unidades (máximo permitido). Esta línea ya está completa.`,
          quantityToAdd,
          targetOrder,
          key,
        };
      }

      return {
        success: true,
        message: `Se agregaron ${quantityToAdd} unidades correctamente`,
        quantityToAdd,
        targetOrder,
        key,
      };
    },
    [orders, findFirstOrderNeedingProduct, assignedQuantities],
  );

  // Revierte una cantidad agregada (cuando la API falla)
  const revertAssignedQuantity = useCallback((key: string, quantityToRevert: number, batchId?: number) => {
    setAssignedQuantities((prev) => ({
      ...prev,
      [key]: Math.max(0, (prev[key] || 0) - quantityToRevert),
    }));
    if (batchId != null && batchId > 0) {
      const bKey = batchAllocKey(key, batchId);
      setBatchAllocations((prev) => {
        const next = { ...prev };
        const v = Math.max(0, (next[bKey] || 0) - quantityToRevert);
        if (v <= 0) delete next[bKey];
        else next[bKey] = v;
        return next;
      });
    }
  }, []);

  // Obtiene el faltante total para un producto en la orden (suma de todas las líneas)
  const getMissingQtyFor = useCallback(
    (productCode: string): number => {
      const order = selectedOrder || findFirstOrderNeedingProduct(productCode);
      if (!order) return 0;

      let totalRemaining = 0;

      // Buscar en todas las líneas de productos individuales
      (order.lines || []).forEach((line) => {
        if (packingScanCodesMatch(line.product?.code, productCode)) {
          const key = packingAssignKey(order.id, line.id, productCode);
          const alreadyAssigned = assignedQuantities[key] || 0;
          const remaining = (line.qty || 0) - alreadyAssigned;
          totalRemaining += Math.max(0, remaining);
        }
      });

      // Buscar en todas las líneas de packs
      (order.lines || []).forEach((line) => {
        if (line.pack && line.pack.lines) {
          const packProduct = line.pack.lines.find((packLine) =>
            packingScanCodesMatch(packLine.product?.code, productCode),
          );
          if (packProduct) {
            // Para packs, incluir productCode en la key
            const key = packingAssignKey(order.id, line.id, productCode);
            const alreadyAssigned = assignedQuantities[key] || 0;
            // La cantidad esperada es cantidad del producto × cantidad de packs
            const expectedQty = (packProduct.qty || 1) * (line.qty || 1);
            const remaining = expectedQty - alreadyAssigned;
            totalRemaining += Math.max(0, remaining);
          }
        }
      });

      return totalRemaining;
    },
    [selectedOrder, findFirstOrderNeedingProduct, assignedQuantities],
  );

  // Procesar código escaneado
  const processScannedCode = useCallback(
    async (scannedCode: string): Promise<{ success: boolean; message: string }> => {
      const trimmedCode = scannedCode.trim();

      try {
        // Limpiar error previo al procesar un nuevo código
        if (packingError) {
          setPackingError(null);
        }

        if (!preparation) {
          return {
            success: false,
            message: 'No hay preparación cargada',
          };
        }

        console.log('🔍 Procesando código escaneado:', trimmedCode);

        // Buscar el producto en la preparación
        const foundLine = preparation.lines.find(
          (line) =>
            packingScanCodesMatch(line.product?.code, trimmedCode) ||
            packingScanCodesMatch(line.product?.ean, trimmedCode),
        );

        if (!foundLine) {
          // Escaneo de ubicación del puesto (customer_box / marketing_item)
          if (isPackingMode && selectedOrder && packingStation) {
            const stationLocation = findStationLocationByCode(packingStation.locations, trimmedCode);
            if (stationLocation) {
              const stationMatch = findRequirementForStationLocation(
                stationLocation,
                selectedOrder,
                packagings,
                assignedPackagingQuantities,
              );

              if (!stationMatch) {
                const packagingName = stationLocation.packaging?.name || 'desconocido';
                return {
                  success: false,
                  message: `La ubicación no contiene un packaging válido para este pedido (${packagingName})`,
                };
              }

              const key = packagingAssignKey(selectedOrder.id, stationMatch.packagingIdToAssign);
              setAssignedPackagingQuantities((prev) => ({
                ...prev,
                [key]: (prev[key] || 0) + 1,
              }));
              setAssignedPackagingPayloadItems((prev) => ({
                ...prev,
                [key]: stationMatch.payloadItem,
              }));
              setAssignedPackagingScanDetails((prev) => ({
                ...prev,
                [packagingScanDetailKey(selectedOrder.id, stationMatch.packagingIdToAssign)]:
                  stationMatch.displayOption,
              }));

              const label = stationMatch.displayOption.name || stationLocation.packaging?.name || trimmedCode;
              return {
                success: true,
                message: `Ubicación ${trimmedCode}: ${label} escaneado correctamente`,
              };
            }
          }

          // Si no es un producto, intentar como packaging del pedido actual (own_box)
          if (isPackingMode && selectedOrder && packagings.length > 0) {
            const scannedPackaging = findPackagingByCode(packagings, trimmedCode);
            if (scannedPackaging) {
              const requirementMatch = findRequirementToFulfill(
                scannedPackaging,
                selectedOrder,
                packagings,
                assignedPackagingQuantities,
              );

              if (!requirementMatch) {
                const pendingStationReqs = computeOrderPackagingRequirements(selectedOrder, packagings).some(
                  (req) =>
                    requirementUsesStationScan(req) &&
                    sumAssignedForRequirement(req, selectedOrder.id, assignedPackagingQuantities) < req.qty,
                );
                if (pendingStationReqs) {
                  return {
                    success: false,
                    message: `Escanea la ubicación del puesto, no el código del packaging "${scannedPackaging.name}"`,
                  };
                }
                return {
                  success: false,
                  message: `El packaging "${scannedPackaging.name}" no es requerido o ya está completo`,
                };
              }

              // El location_id DEBE venir del catálogo /packaging del item escaneado (locations[].location_id).
              // No usamos preparation.location_id (ese es el carrito, no la ubicación de la caja).
              const scannedLocationId = scannedPackaging.locations?.[0]?.location_id;
              const payloadItem =
                requirementMatch.payloadItem ||
                (scannedLocationId != null
                  ? {
                      packaging_id: requirementMatch.payloadPackagingId,
                      location_id: scannedLocationId,
                    }
                  : null);

              if (!payloadItem) {
                return {
                  success: false,
                  message: `El packaging "${scannedPackaging.name}" no tiene ubicación (locations) disponible en /packaging. Pide al 3PL que asigne stock/ubicación.`,
                };
              }

              const key = packagingAssignKey(selectedOrder.id, requirementMatch.packagingIdToAssign);
              setAssignedPackagingQuantities((prev) => ({
                ...prev,
                [key]: (prev[key] || 0) + 1,
              }));
              setAssignedPackagingPayloadItems((prev) => ({
                ...prev,
                [key]: payloadItem,
              }));

              return {
                success: true,
                message: `Packaging ${scannedPackaging.name} escaneado correctamente`,
              };
            }
          }

          return {
            success: false,
            message: `Producto con código "${trimmedCode}" no encontrado en esta preparación`,
          };
        }

        const productId = foundLine.product_id;
        const orderForPicks: Order | null = isPackingMode ? selectedOrder : findFirstOrderNeedingProduct(trimmedCode);

        const picksForProduct =
          orderForPicks && preparation
            ? filterLinePicksForOrder(preparation.line_picks, preparation, orderForPicks).filter(
                (p) => p.product_id === productId,
              )
            : [];
        const batchOptions = uniqueBatchesFromPicks(picksForProduct);
        let scanBatchId: number | undefined;
        if (batchOptions.length === 1) {
          scanBatchId = batchOptions[0].batch_id;
        } else if (batchOptions.length > 1) {
          const chosen = activeBatchIdByProduct[productId];
          if (chosen == null) {
            // Entrar en modo packing para mostrar el selector de lote en la UI
            if (!isPackingMode) {
              selectProduct(trimmedCode);
            }
            return {
              success: false,
              message: 'Hay varios lotes para este producto. Selecciona un lote antes de escanear.',
            };
          }
          scanBatchId = chosen;
        }

        // Si no estamos en modo packing, seleccionar el producto, entrar en modo packing Y sumar +1
        if (!isPackingMode) {
          selectProduct(trimmedCode);
          // Agregar automáticamente +1 unidad del producto escaneado
          const addResult = addToPacking(trimmedCode, 1, undefined, scanBatchId ?? null);

          if (!addResult.success) {
            return {
              success: false,
              message: `Error al agregar producto: ${addResult.message}`,
            };
          }

          // Guardar la última cantidad agregada para poder revertirla si la API falla
          if (addResult.key && addResult.quantityToAdd) {
            setLastAddedQuantity({
              key: addResult.key,
              quantity: addResult.quantityToAdd,
              ...(scanBatchId != null ? { batchId: scanBatchId } : {}),
            });
          }

          return {
            success: true,
            message: `Producto ${foundLine.product?.name || 'encontrado'}. Seleccionado pedido y ${addResult.message.toLowerCase()}`,
          };
        }

        // Si estamos en modo packing, agregar cantidad al packing
        const addResult = addToPacking(trimmedCode, 1, selectedOrder?.id, scanBatchId ?? null);

        if (!addResult.success) {
          return {
            success: false,
            message: addResult.message,
          };
        }

        // Guardar la última cantidad agregada para poder revertirla si la API falla
        if (addResult.key && addResult.quantityToAdd) {
          setLastAddedQuantity({
            key: addResult.key,
            quantity: addResult.quantityToAdd,
            ...(scanBatchId != null ? { batchId: scanBatchId } : {}),
          });
        }

        const productName = foundLine.product?.name || 'Producto';
        return {
          success: true,
          message: `${productName}: ${addResult.message}`,
        };
      } catch (error) {
        console.error('❌ Error procesando código:', error);
        return {
          success: false,
          message: 'Error al procesar el código escaneado',
        };
      }
    },
    [
      preparation,
      isPackingMode,
      selectProduct,
      addToPacking,
      selectedOrder,
      packingError,
      findFirstOrderNeedingProduct,
      activeBatchIdByProduct,
      packagings,
      assignedPackagingQuantities,
      packingStation,
    ],
  );

  // Limpiar error de packing
  const clearPackingError = useCallback(() => {
    setPackingError(null);
  }, []);

  // Salir del modo packing
  const exitPackingMode = useCallback(() => {
    setIsPackingMode(false);
    setSelectedOrder(null);
    setSelectedProduct(null);
    setCompletedOrder(null); // Limpiar el mensaje de completado
    setIsSendingOrder(false);
    setPackingError(null); // Limpiar errores
    setLastAddedQuantity(null); // Limpiar última cantidad agregada
    setAssignedQuantities({}); // Reiniciar contador de productos del pedido
    setAssignedPackagingQuantities({}); // Reiniciar contador de packagings escaneados
    setAssignedPackagingPayloadItems({});
    setAssignedPackagingScanDetails({});
    setBatchAllocations({});
    setActiveBatchIdByProduct({});
  }, []);

  // Enviar datos del pedido completado (solo cuando el pedido esté completo)
  const sendOrderPackingData = useCallback(
    async (orderId: number) => {
      if (!preparation?.id) return;

      console.log('🔍 Enviando datos del pedido completado:', orderId);

      // Procesar datos del pedido respetando las líneas originales
      const targetOrder = orders?.find((o) => o.id === orderId);
      if (!targetOrder) return;

      const orderProducts: { product_id: number; quantity: number; batch_id?: number }[] = [];

      const pushFromLineKey = (lineAssignKey: string, productId: number, assignedQuantity: number) => {
        if (assignedQuantity <= 0) return;
        const batchParts = Object.entries(batchAllocations)
          .filter(([k, v]) => k.startsWith(`${lineAssignKey}@@`) && v > 0)
          .map(([k, v]) => ({ batchId: Number(k.split('@@')[1]), qty: v }));
        if (batchParts.length === 0) {
          orderProducts.push({ product_id: productId, quantity: assignedQuantity });
        } else {
          for (const { batchId, qty } of batchParts) {
            orderProducts.push({ product_id: productId, quantity: qty, batch_id: batchId });
          }
        }
      };

      (targetOrder.lines || []).forEach((line) => {
        if (line.pack && line.pack.lines) {
          line.pack.lines.forEach((packLine) => {
            if (packLine.product) {
              const productCode = packLine.product.code?.trim() || '';
              const key = packingAssignKey(orderId, line.id, productCode);
              const assignedQuantity = assignedQuantities[key] || 0;
              pushFromLineKey(key, packLine.product.id, assignedQuantity);
            }
          });
        } else if (line.product) {
          const productCode = line.product.code?.trim() || '';
          const key = packingAssignKey(orderId, line.id, productCode);
          const assignedQuantity = assignedQuantities[key] || 0;
          pushFromLineKey(key, line.product.id, assignedQuantity);
        }
      });

      if (orderProducts.length === 0) return;

      if (packHttpInFlightRef.current) return;
      packHttpInFlightRef.current = true;

      try {
        setIsSendingOrder(true);
        setPackingError(null); // Limpiar errores previos

        // Determinar el código de impresora
        let printerCode: string;

        // Intentar obtener la impresora seleccionada desde localStorage
        const selectedPrinterId = localStorage.getItem('selectedPrinterId');
        if (selectedPrinterId) {
          printerCode = selectedPrinterId;
        } else {
          // Si no hay impresora seleccionada, usar PDF como fallback
          printerCode = 'pdf';
        }

        // Construir array de packagings actualmente escaneados para el pedido
        const packagingPrefix = `${orderId}:pkg:`;
        const packagingPayloadFromScans: PackingPayloadPackagingItem[] = Object.entries(assignedPackagingQuantities)
          .filter(([key, qty]) => key.startsWith(packagingPrefix) && qty > 0 && assignedPackagingPayloadItems[key])
          .map(([key, qty]) => ({
            ...assignedPackagingPayloadItems[key],
            qty,
          }));
        const packagingPayload =
          packagingPayloadFromScans.length > 0
            ? packagingPayloadFromScans
            : buildPackagingPayload(targetOrder, orderId, packagings, assignedPackagingQuantities);

        const packingStationId = orderRequiresPackingStation(targetOrder, packagings) ? packingStation?.id : undefined;

        if (orderRequiresPackingStation(targetOrder, packagings) && packingStationId == null) {
          throw new Error('Debes escanear el puesto de trabajo antes de empaquetar este pedido.');
        }

        console.log('🔍 Packing payload final:', {
          orderId,
          assignedPackagingQuantities,
          assignedPackagingPayloadItems,
          packagingPayload,
          orderProducts,
        });

        // Si está activo el modo temporal, omite el envío real a la API.
        if (TEMP_DISABLE_PACKING_API) {
          console.log('🧪 Packing API desactivada temporalmente. No se envía /pack.', {
            preparationId: preparation.id,
            orderId,
            orderProducts,
            printerCode,
            packagingPayload,
          });
        } else {
          // Enviar datos a la API
          await packingService.adapter.sendOrderPackingData(
            preparation.id,
            orderId,
            orderProducts,
            printerCode,
            packagingPayload.length > 0 ? packagingPayload : undefined,
            orderHasAttachments(targetOrder) ? (attachmentPrinterCode ?? undefined) : undefined,
            packingStationId,
          );
        }

        console.log('✅ Datos del pedido enviados exitosamente');

        packedPendingRefreshRef.current.add(orderId);

        // Salir inmediatamente del modo packing
        setIsPackingMode(false);
        setSelectedOrder(null);
        setSelectedProduct(null);

        // Limpiar cantidades asignadas para este pedido (solo en memoria)
        const newAssignedQuantities = { ...assignedQuantities };
        Object.keys(newAssignedQuantities).forEach((key) => {
          if (key.startsWith(`${orderId}:`)) {
            delete newAssignedQuantities[key];
          }
        });
        setAssignedQuantities(newAssignedQuantities);

        const newAssignedPackagings = { ...assignedPackagingQuantities };
        Object.keys(newAssignedPackagings).forEach((key) => {
          if (key.startsWith(`${orderId}:`)) {
            delete newAssignedPackagings[key];
          }
        });
        setAssignedPackagingQuantities(newAssignedPackagings);

        const newAssignedPackagingPayloadItems = { ...assignedPackagingPayloadItems };
        Object.keys(newAssignedPackagingPayloadItems).forEach((key) => {
          if (key.startsWith(`${orderId}:`)) {
            delete newAssignedPackagingPayloadItems[key];
          }
        });
        setAssignedPackagingPayloadItems(newAssignedPackagingPayloadItems);

        const newAssignedPackagingScanDetails = { ...assignedPackagingScanDetails };
        Object.keys(newAssignedPackagingScanDetails).forEach((key) => {
          if (key.startsWith(`${orderId}:`)) {
            delete newAssignedPackagingScanDetails[key];
          }
        });
        setAssignedPackagingScanDetails(newAssignedPackagingScanDetails);

        const newBatchAlloc = { ...batchAllocations };
        Object.keys(newBatchAlloc).forEach((key) => {
          if (key.startsWith(`${orderId}:`)) {
            delete newBatchAlloc[key];
          }
        });
        setBatchAllocations(newBatchAlloc);
        setActiveBatchIdByProduct({});

        // Limpiar la última cantidad agregada (ya confirmada por la API)
        setLastAddedQuantity(null);

        // Refrescar pedidos antes de actualizar el banner local
        if (refreshData) {
          await refreshData();
        }

        // Forzamos el estado FULL localmente para que el banner se muestre
        // aunque la respuesta refrescada aún no haya llegado al componente.
        const completedOrderData = orders?.find((o) => o.id === orderId);
        if (completedOrderData) {
          const completedAt = new Date().toISOString();
          setCompletedOrder({
            ...completedOrderData,
            status_id: 6,
            processed_at: completedOrderData.processed_at || completedAt,
            shipment: {
              ...completedOrderData.shipment,
              processed_at: completedOrderData.shipment?.processed_at || completedAt,
            },
          });
        }
      } catch (error: any) {
        console.error('❌ Error enviando datos del pedido:', error);

        // Revertir la última cantidad agregada para permitir retry natural
        if (lastAddedQuantity) {
          console.log('🔄 Revirtiendo última cantidad agregada:', lastAddedQuantity);
          revertAssignedQuantity(lastAddedQuantity.key, lastAddedQuantity.quantity, lastAddedQuantity.batchId);
          setLastAddedQuantity(null);
        }

        // Establecer el mensaje de error para mostrar al usuario
        const errorMessage = error?.message || 'Error al enviar los datos del pedido';
        setPackingError(errorMessage);
        toast({
          title: 'Error en la operación',
          description: errorMessage,
          variant: 'destructive',
        });
        // En caso de error, mantener todos los estados para que el usuario pueda reintentar
        // No cambiar isPackingMode, selectedOrder, ni limpiar assignedQuantities
      } finally {
        packHttpInFlightRef.current = false;
        setIsSendingOrder(false);
      }
    },
    [
      preparation?.id,
      assignedQuantities,
      assignedPackagingQuantities,
      assignedPackagingPayloadItems,
      batchAllocations,
      orders,
      packagings,
      packingService,
      refreshData,
      lastAddedQuantity,
      revertAssignedQuantity,
      toast,
      attachmentPrinterCode,
      packingStation,
      assignedPackagingScanDetails,
    ],
  );

  // Detectar cuando la orden actual se completa (todas las líneas sin faltante) y enviar
  useEffect(() => {
    if (!selectedOrder) return;
    if (selectedOrder.shipment?.processed_at) return;

    const lines: any[] = selectedOrder.lines || [];
    if (lines.length === 0) return;

    const hasRemaining = lines.some((line) => {
      if (line.pack && line.pack.lines) {
        return line.pack.lines.some((packLine: any) => {
          const productCode = packLine.product?.code?.trim() || '';
          const key = packingAssignKey(selectedOrder.id, line.id, productCode);
          const assigned = assignedQuantities[key] || 0;
          const expectedQty = (packLine.qty || 1) * (line.qty || 1);
          return expectedQty - assigned > 0;
        });
      } else if (line.product) {
        const productCode = line.product?.code?.trim() || '';
        const key = packingAssignKey(selectedOrder.id, line.id, productCode);
        const assigned = assignedQuantities[key] || 0;
        return (line.qty || 0) - assigned > 0;
      }
    });

    const packagingsCompleted = areAllPackagingsCompleted(selectedOrder, packagings, assignedPackagingQuantities);

    if (!hasRemaining && packagingsCompleted && !isSendingOrder && !packingError) {
      sendOrderPackingData(selectedOrder.id);
    }
  }, [
    assignedQuantities,
    assignedPackagingQuantities,
    selectedOrder,
    sendOrderPackingData,
    isSendingOrder,
    packingError,
    packagings,
  ]);

  // Efecto para limpiar estados cuando se sale del modo packing
  useEffect(() => {
    if (!isPackingMode) {
      // Limpiar estados adicionales cuando se sale del modo packing
      setIsSendingOrder(false);
      setSelectedOrder(null);
      setSelectedProduct(null);
    }
  }, [isPackingMode]);

  const setActiveBatchIdForProduct = useCallback((productId: number, batchId: number | null) => {
    setActiveBatchIdByProduct((prev) => ({ ...prev, [productId]: batchId }));
  }, []);

  return {
    isPackingMode,
    selectedProduct,
    selectedOrder,
    assignedQuantities,
    assignedPackagingQuantities,
    assignedPackagingScanDetails,
    completedOrder,
    isSendingOrder,
    packingError,
    activeBatchIdByProduct,
    setActiveBatchIdForProduct,
    selectProduct,
    addToPacking,
    getMissingQtyFor,
    exitPackingMode,
    processScannedCode,
    clearPackingError,
  };
};
